import {
  configureLocusAxiosInstance,
  configureOndcAxiosInstance,
} from '@dehaat/kisan-app-bl/axios/configure'
import configureGraphQlClient from '@dehaat/kisan-app-bl/graphql'
import Axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'

import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '@/constants/common'
import {
  AUTH_TOKENS_UPDATED_EVENT,
  triggerLogout,
} from '@/utils/auth/handle401Refresh'
import refreshCoordinator from '@/utils/auth/refreshCoordinator'
import { getCookieValue, persistToken } from '@/utils/helper'

import { khetiConfig } from './khetiConfig'
import { locusConfig } from './locusConfig'
import { OndcConfig } from './ondcConfig'

// GraphQl client for client-side config
configureGraphQlClient({
  endPoint: `${process.env.NEXT_PUBLIC_API_BASE_URL}/graphql/`,
})

configureOndcAxiosInstance(OndcConfig)
configureLocusAxiosInstance(locusConfig)

const axios = Axios.create({
  baseURL: khetiConfig.khetiUrl,
})

axios.defaults.headers.common['Access-Control-Allow-Origin'] = '*'
axios.defaults.headers.post['Content-Type'] = 'application/json'
axios.defaults.headers.common['APP-CODE'] = khetiConfig.appCode

/** Refresh endpoint — 401s from this URL must NOT be intercepted */
const REFRESH_ENDPOINT = '/api/keycloak/token'

/** Queue of requests waiting for a token refresh to complete */
let failedQueue: {
  resolve: (value: unknown) => void
  reject: (reason?: unknown) => void
  config: InternalAxiosRequestConfig
}[] = []

let isRefreshing = false

function processQueue(error: unknown, newAccessToken: string | null) {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error)
    } else {
      // Retry the original request with the new access token
      config.headers.Authorization = `Bearer ${newAccessToken}`
      resolve(axios(config))
    }
  })
  failedQueue = []
}

axios.interceptors.request.use(
  async (config) => {
    const copy = { ...config }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    khetiConfig.requestInterceptorSuccessCb!(copy)
    return config
  },
  (error) => Promise.reject(error),
)

axios.interceptors.response.use(
  (response) => {
    if (response.status >= 400) {
      return response
    }
    if (response.data.error == null) {
      return response
    }
    return Promise.reject(response.data.error)
  },
  (error: AxiosError) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    khetiConfig.responseInterceptorErrCb!(error)

    const originalRequest = error.config as InternalAxiosRequestConfig | undefined

    // Only handle 401s on non-refresh endpoints, client-side only
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest.url?.includes(REFRESH_ENDPOINT)
    ) {
      // Queue this request and wait for the refresh to complete
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, config: originalRequest })
        })
      }

      isRefreshing = true

      const currentRefreshToken = getCookieValue(REFRESH_TOKEN_KEY)

      if (!currentRefreshToken) {
        isRefreshing = false
        triggerLogout()
        return Promise.reject(error)
      }

      return new Promise((resolve, reject) => {
        refreshCoordinator
          .refresh(currentRefreshToken)
          .then((authToken) => {
            const newAccessToken = authToken.access_token
            // Persist refreshed tokens to cookies so they stay in sync
            persistToken(ACCESS_TOKEN_KEY, authToken.access_token, authToken.expires_in)
            persistToken(REFRESH_TOKEN_KEY, authToken.refresh_token, authToken.refresh_expires_in)
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent(AUTH_TOKENS_UPDATED_EVENT))
            }
            // Retry all queued requests with the new token
            processQueue(null, newAccessToken)
            // Retry the original request
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
            resolve(axios(originalRequest))
          })
          .catch((refreshError) => {
            processQueue(refreshError, null)
            triggerLogout()
            reject(refreshError)
          })
          .finally(() => {
            isRefreshing = false
          })
      })
    }

    return Promise.reject(error)
  },
)

export default axios
